/**
 * 18. Chart of Accounts — tree table of accounts; status, account type,
 * default tax; create/edit in drawer.
 *
 * Arabic and English (C7): every word from the catalogs; an account type and
 * a normal balance are the glossary's words, never the stored value. Account
 * names are the workspace's own data.
 */
import { Card, PageHeader, DataTable, Skeleton, Badge } from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import type { BadgeTone } from "@/components/ui";
import { useApiList, useApiQuery } from "@/lib/api/hooks";
import { CreateRecordButton } from "@/components/CreateRecordButton";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { valueLabel } from "@/lib/i18n/records";

interface Account {
  id: string;
  code: string;
  name: string;
  accountType: "Asset" | "Liability" | "Equity" | "Revenue" | "Expense";
  normalBalance: "Debit" | "Credit";
  isPostable: boolean;
  isActive: boolean;
  parentCode: string | null;
}

const ACCOUNT_TYPES: Array<Account["accountType"]> = [
  "Asset",
  "Liability",
  "Equity",
  "Revenue",
  "Expense",
];

const TYPE_TONES: Record<Account["accountType"], BadgeTone> = {
  Asset: "primary",
  Liability: "warning",
  Equity: "default",
  Revenue: "success",
  Expense: "danger",
};

interface LookupOption {
  id: string;
  name?: string;
  code?: string;
}

export function ChartOfAccountsPage() {
  const { t } = useTranslation();
  const legalEntities = useApiQuery<LookupOption[]>(
    ["legalEntities"],
    "/legal-entities",
  );
  const { data, isLoading } = useApiList<Account>(
    ["finance", "accounts"],
    "/finance/accounts",
  );

  return (
    <div>
      <PageHeader
        title={t("finance.accounts.title")}
        subtitle={t("finance.accounts.subtitle")}
        actions={
          <>
            <PendingButton reason="dataImport" slice="F14-W">
              {t("finance.accounts.import")}
            </PendingButton>
            <CreateRecordButton
              label={t("finance.accounts.new")}
              path="/accounts"
              invalidate={["finance", "accounts"]}
              fields={[
                {
                  name: "legalEntityId",
                  label: t("fields.legalEntity"),
                  type: "select",
                  required: true,
                  options: (legalEntities.data ?? []).map((e) => ({
                    value: e.id,
                    label: e.name ?? e.code ?? e.id,
                  })),
                },
                {
                  name: "code",
                  label: t("finance.accounts.accountCode"),
                  required: true,
                },
                { name: "name", label: t("fields.name"), required: true },
                {
                  name: "accountType",
                  label: t("fields.type"),
                  type: "select",
                  required: true,
                  options: ACCOUNT_TYPES.map((type) => ({
                    value: type,
                    label: valueLabel(t, "accountType", type),
                  })),
                },
              ]}
            />
          </>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("finance.accounts.list"), data.total)}>
          <DataTable<Account>
            rows={data.items}
            columns={[
              {
                key: "code",
                label: t("fields.code"),
                render: (r) => <span className="atlas-mono">{r.code}</span>,
              },
              { key: "name", label: t("fields.name") },
              {
                key: "accountType",
                label: t("fields.type"),
                render: (r) => (
                  <Badge tone={TYPE_TONES[r.accountType]}>
                    {valueLabel(t, "accountType", r.accountType)}
                  </Badge>
                ),
              },
              {
                key: "normalBalance",
                label: t("finance.accounts.normalBalance"),
                render: (r) =>
                  r.normalBalance
                    ? valueLabel(t, "normalBalance", r.normalBalance)
                    : "—",
              },
              {
                key: "isPostable",
                label: t("finance.accounts.postable"),
                render: (r) =>
                  r.isPostable ? (
                    <Badge tone="success">{t("shared.yes")}</Badge>
                  ) : (
                    "—"
                  ),
              },
              {
                key: "isActive",
                label: t("fields.status"),
                render: (r) =>
                  r.isActive ? (
                    <Badge tone="success">{t("fields.active")}</Badge>
                  ) : (
                    <Badge tone="default">
                      {t("finance.accounts.inactive")}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
